import { almostZero } from "@xola/ui-kit";
import { SetFilterValuesFuncParams } from "ag-grid-enterprise";
import { DateFormat, formatDate, isPartnerPurchase, Loader } from "common";
import { get } from "get-wild";
import { isNumber, sumBy, uniq } from "lodash";
import pluralize from "pluralize";
import React from "react";
import {
    Coupon,
    CouponType,
    LineItemType,
    Permission,
    PurchaseItem,
    Report,
    Transaction,
    TransactionItemFee,
} from "types";
import { Currency } from "../../../Common/Currency";
import { NoTranslate } from "../../../Common/NoTranslate";
import { PaymentMethodIcon } from "../../../Common/PaymentMethodIcon";
import { MultipleArrivalDateRenderer, MultipleProductNameRenderer, SourceCellRenderer } from "../ColumnTypes";
import { CustomReport } from "../CustomReport";
import { CustomReportGridContext, ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { isAmountIncludesPartnerFee, showPartnerFee } from "@/models/PartnerFeeFormula";
import { getAllSellerPaymentMethods, getPaymentMethodName } from "@/models/Payment";
import { isPartnerReconcileTransaction } from "@/models/Transaction";

interface TransactionReportProps {
    report: Report;
}

interface VoucherWithPaymentMethod extends Coupon {
    paymentMethod: string;
}

const isVoucherWithPaymentMethod = (coupon: Coupon): coupon is VoucherWithPaymentMethod => {
    return coupon.type === CouponType.VOUCHER && !!coupon.paymentMethod;
};

export const TransactionReport = ({ report }: TransactionReportProps) => {
    const { isGranted } = useAuthorization();
    const showMerchandiseFee = isGranted(Permission.MERCHANDISE_VIEW);

    const { data: coupons, isLoading } = useAutoFetchCollection<Coupon>(
        "coupons",
        { source: "xola" },
        { enabled: isGranted(Permission.COUPONS_VIEW) },
    );

    if (isLoading) {
        return <Loader />;
    }

    const paymentMethods = uniq(
        coupons.filter((coupon) => isVoucherWithPaymentMethod(coupon)).map((voucher) => voucher.paymentMethod),
    );

    return (
        <CustomReport
            dateFields={["createdAt", "items.realizedAt"]}
            report={report}
            columns={columns(paymentMethods as string[], report, showMerchandiseFee)}
        />
    );
};

const columns = (paymentMethods: string[], report: Report, showMerchandiseFee: boolean): ReportColumnDef[] => {
    return [
        {
            field: "id",
            headerName: "Transaction ID",
            type: "objectid",
            cellClass: "no-translate",
        },
        {
            field: "createdAt",
            headerName: "Transaction Date",
            type: "datetime",
            filterValueType: ReportColumnFilterValueType.DATE,
            filterParams: {
                report,
            },
            cellClass: "no-translate",
        },
        {
            field: "items.realizedAt",
            headerName: "Realized Date",
            maxWidth: 150,
            type: "datetime",
            filterValueType: ReportColumnFilterValueType.DATE,
            filterParams: {
                report,
            },
            cellRenderer: ({ data: transaction }: { data: Transaction }) => {
                const dates = uniq<string>(get(transaction.items, "*.realizedAt")).filter((date) => !!date);
                const realizedAtValue = dates[0];
                if (realizedAtValue) {
                    const realizedAt = formatDate(realizedAtValue, DateFormat.DATE_SHORT);

                    return dates.length === 1 ? (
                        <NoTranslate>{realizedAt}</NoTranslate>
                    ) : (
                        <MultipleArrivalDateRenderer dates={dates} />
                    );
                }

                return "";
            },
        },
        {
            field: "purchase.items.arrival",
            headerName: "Arrival Date",
            filter: false,
            maxWidth: 150,
            cellRenderer: ({ data: transaction }: { data: Transaction }) => {
                const dates: string[] = (uniq<string>(get(transaction, "purchase.items.*.arrivalDate")) ?? []).filter(
                    (date) => !!date,
                );

                if (dates.length === 0) {
                    return "";
                }

                if (dates.length === 1) {
                    return <NoTranslate>{formatDate(dates[0], DateFormat.DATE_SHORT)}</NoTranslate>;
                }

                return <MultipleArrivalDateRenderer dates={dates} />;
            },
        },
        {
            field: "items.0.orderItem.product.id",
            headerName: "Product",
            type: "product",
            maxWidth: 250,
            cellClass: "no-translate",
            cellRenderer: MultipleProductNameRenderer,
            valueGetter: ({ data }: { data: Transaction }) => {
                const { items } = data;
                const productIds = [];

                for (const item of items) {
                    // collate seller's own items in purchase
                    if (item.orderItem?.product?.id) {
                        productIds.push(item.orderItem.product.id);
                    }
                }

                let partnerProductsLabel = "Partner product";
                if (data.purchase && isPartnerPurchase(data.purchase)) {
                    const partnerProducts = data.purchase.lineItems.filter((lineItem) =>
                        [LineItemType.PARTNER_PURCHASE_SUMMARY_ITEM, LineItemType.PARTNER_PURCHASE_ITEM].includes(
                            lineItem.type,
                        ),
                    );
                    partnerProductsLabel = "Partner " + pluralize("product", partnerProducts.length);
                    // check if there are source items and append to the ids array
                    productIds.push(partnerProductsLabel);
                } else if (isPartnerReconcileTransaction(data)) {
                    productIds.push(partnerProductsLabel);
                }
                return productIds.join(",");
            },
        },
        {
            field: "purchase.customerName",
            headerName: "Customer Name",
            minWidth: 200,
            cellClass: "no-translate",
        },
        {
            field: "currency",
            headerName: "Currency",
            maxWidth: 120,
            cellClass: "no-translate",
        },
        {
            field: "items.gross",
            headerName: "Gross",
            type: ["currency", "firstElement"],
            filterParams: {
                maxNumConditions: 1,
            },
            valueGetter: ({ data }: { data: Transaction }) => {
                const { items } = data;
                if (items.length > 0) {
                    return sumBy(items, "gross");
                }

                return 0;
            },
        },
        {
            field: "items.commission",
            headerName: "Processing Fee",
            type: ["currency", "firstElement"],
            filterParams: {
                maxNumConditions: 1,
            },
            valueGetter: ({ data }: { data: Transaction }) => {
                const { items } = data;
                if (items.length > 0) {
                    return sumBy(items, "commission");
                }

                return 0;
            },
        },
        {
            field: "items.commissionSplitAmount.seller",
            headerName: "Service Fee",
            type: "currency",
            filter: false,
            hide: !showPartnerFee(),
            valueGetter: ({ data, context }: { data: Transaction; context: CustomReportGridContext }) => {
                const hasCommissionSplitAmount = data.items.some((item) => item.commissionSplitAmount);
                if (!hasCommissionSplitAmount) {
                    return 0;
                }

                const commissionSplitAmount = sumBy(data.items, (item) => item.commissionSplitAmount?.seller ?? 0);

                if (commissionSplitAmount > 0) {
                    return commissionSplitAmount;
                }

                const travelerAmount = sumBy(data.items, (item) => item.commissionSplitAmount?.traveler ?? 0);
                if (isAmountIncludesPartnerFee(context.seller.partnerFeeFormulas) && isNumber(travelerAmount)) {
                    return travelerAmount;
                }

                return 0;
            },
        },
        {
            field: "items.commissionSplitAmount.offline",
            headerName: "Guest Fee",
            type: "currency",
            filter: false,
            valueGetter: ({ data }: { data: Transaction }) => {
                const item = data.items[0];
                if (item?.commissionSplitAmount && item.commissionSplitAmount.offline > 0) {
                    return item.commissionSplitAmount.offline;
                }

                return 0;
            },
        },
        {
            field: "items.commissionSplitAmount.ware",
            headerName: "Merchandise Fee",
            type: "currency",
            filter: false,
            hide: !showMerchandiseFee,
            valueGetter: ({ data }: { data: Transaction }) => {
                return sumBy(data.items, (item) => item.commissionSplitAmount?.ware ?? 0);
            },
        },
        {
            field: "items.net",
            headerName: "Net",
            type: ["currency", "firstElement"],
            filterParams: {
                maxNumConditions: 1,
            },
            valueGetter: ({ data }: { data: Transaction }) => {
                const { items } = data;
                if (items.length > 0) {
                    return sumBy(items, "net");
                }

                return 0;
            },
        },
        {
            field: "balance",
            headerName: "Balance",
            type: "currency",
            filterParams: {
                maxNumConditions: 1,
            },
        },
        {
            field: "source",
            headerName: "Source",
            type: "source",
            cellRenderer: SourceCellRenderer,
        },
        {
            field: "method",
            width: 350,
            headerName: "Method",
            filter: "agSetColumnFilter",
            filterParams: {
                values: (params: SetFilterValuesFuncParams) => {
                    const methods = getAllSellerPaymentMethods(params.context.seller);
                    const allMethods = [...methods.map((method) => method.id), ...paymentMethods];
                    params.success(allMethods); // Use `id` instead of `name` for query params
                },
                cellRenderer: ({ value, context }: { value: string; context: CustomReportGridContext }) => {
                    if (typeof value === "string" && /select all/i.test(value)) {
                        return value;
                    }

                    return <span>{getPaymentMethodName(context.seller, value) ?? value}</span>;
                },
            },
            cellRenderer: ({ value, context }: { value: string; context: CustomReportGridContext }) => {
                return (
                    <span>
                        {/* Hide the icon when rendered in the report filter tag */}
                        {context.target === "tag" ? null : <PaymentMethodIcon method={value} className="mr-1" />}
                        {getPaymentMethodName(context.seller, value) ?? value}
                    </span>
                );
            },
        },
        {
            field: "remoteTransactions.cardSummary.brand",
            headerName: "CC Brand",
            type: ["startCase", "firstElement"],
            filter: true,
            minWidth: 160,
            cellClass: "no-translate",
        },
        {
            field: "purchase.id",
            headerName: "Purchase ID",
            type: ["objectid", "purchase"],
            filter: true,
            cellClass: "no-translate",
            valueGetter: ({ data }: { data: Transaction }) => {
                if (data.purchase) {
                    const purchaseId = data.purchase.id;
                    if (isPartnerPurchase(data.purchase) && !data.purchase.items) {
                        return "Partner Purchase";
                    }
                    return purchaseId ?? "";
                }
                if (isPartnerReconcileTransaction(data)) {
                    return "Partner Purchase";
                }
                return "";
            },
        },
        {
            field: "items.confirmationCode",
            headerName: "Confirmation ID",
            maxWidth: 150,
            valueGetter: ({ data }: { data: Transaction }) => {
                const item = getItemForTransaction(data);
                return item && "shortCode" in item ? item.shortCode ?? "" : "";
            },
        },
        {
            field: "fees",
            headerName: "Taxes & Fees Total",
            type: ["currency", "firstElement"],
            minWidth: 170,
            maxWidth: 200,
            filter: false,
            valueGetter: ({ data }: { data: Transaction }) => {
                const { items } = data;
                const amount = sumBy(items, "taxFee");
                return amount;
            },
            expand: {
                key: "items.*.fees.*.name",
                type: ["currency"],
                minWidth: 170,
                maxWidth: 200,
                cellRenderer: ({ data, colDef }: { data: Transaction; colDef: ReportColumnDef }) => {
                    const columnName = colDef.headerName;
                    const fees = get(data, `items.*.fees`);
                    const amount = sumBy(fees, (fee: TransactionItemFee) => (fee.name === columnName ? fee.amount : 0));
                    return almostZero(amount) ? "" : <Currency shouldRemoveTrailingZeroes={false}>{amount}</Currency>;
                },
            },
        },
        {
            field: "purchase.tags",
            headerName: "Tags",
            type: "tags",
            cellClass: "no-translate",
        },
        {
            field: "payout.createdAt",
            headerName: "Payout Date",
            type: "datetime",
            filter: false,
            cellRenderer: ({ data }: { data: Transaction }) => {
                const { payout } = data;
                if (payout?.createdAt) {
                    return <NoTranslate>{formatDate(payout.createdAt, DateFormat.DATE_TIME_SHORT)}</NoTranslate>;
                }

                return "";
            },
        },
    ];
};

const getItemForTransaction = (transaction: Transaction): PurchaseItem | undefined => {
    const itemId = transaction.items[0]?.orderItem?.id;
    return transaction.purchase?.items?.find((item: PurchaseItem) => item.id === itemId);
};
